import { Button } from '@/components/ui/button'
import { useRef, useState, useCallback } from 'react'
import { Upload, X, Video, AlertCircle } from 'lucide-react'

import type { VideoAspectRatio, VideoMetadata } from '../clip.types'

// ----------------------------------------------------------------------

interface ClipVideoUploaderProps {
  value: File | null
  onChange: (file: File | null) => void
  onDurationChange?: (durationSec: number) => void
  onMetadataChange?: (metadata: VideoMetadata | null) => void
  /** 0-100 during upload; null when idle */
  uploadProgress?: number | null
  /** Max accepted file size in bytes. Defaults to the legacy 500 MB cap. */
  maxBytes?: number
  /** Locks the picker / drop zone / remove button while an upload is in flight. */
  disabled?: boolean
}

const classifyAspectRatio = (width: number, height: number): VideoMetadata => {
  const ratio = width / height
  const tolerance = 0.03
  let aspectRatio: VideoAspectRatio = 'custom'

  if (Math.abs(ratio - 16 / 9) <= tolerance) {
    aspectRatio = '16:9'
  } else if (Math.abs(ratio - 9 / 16) <= tolerance) {
    aspectRatio = '9:16'
  } else if (Math.abs(ratio - 1) <= tolerance) {
    aspectRatio = '1:1'
  }

  return {
    videoWidth: width,
    videoHeight: height,
    displayAspectRatio: Number(ratio.toFixed(4)),
    aspectRatio,
    orientation:
      Math.abs(ratio - 1) <= tolerance
        ? 'square'
        : ratio > 1
          ? 'landscape'
          : 'portrait',
  }
}

const getAspectLabel = (metadata: VideoMetadata): string => {
  const orientationLabel =
    metadata.orientation === 'landscape'
      ? 'แนวนอน'
      : metadata.orientation === 'portrait'
        ? 'แนวตั้ง'
        : 'จัตุรัส'

  return `${metadata.aspectRatio} ${orientationLabel} · ${metadata.videoWidth}×${metadata.videoHeight}`
}

export default function ClipVideoUploader({
  value,
  onChange,
  onDurationChange,
  onMetadataChange,
  uploadProgress = null,
  maxBytes,
  disabled = false,
}: ClipVideoUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [sizeError, setSizeError] = useState(false)
  const [metadata, setMetadata] = useState<VideoMetadata | null>(null)

  const SIZE_LIMIT_BYTES = maxBytes ?? 500 * 1024 * 1024
  const SIZE_LIMIT_MB = Math.round(SIZE_LIMIT_BYTES / (1024 * 1024))

  const handleFile = useCallback(
    (file: File) => {
      if (file.type !== 'video/mp4') {
        return
      }
      if (file.size > SIZE_LIMIT_BYTES) {
        setSizeError(true)
        return
      }

      setSizeError(false)
      onChange(file)
      setMetadata(null)
      onMetadataChange?.(null)

      const url = URL.createObjectURL(file)
      const video = document.createElement('video')
      video.src = url
      video.muted = true
      video.preload = 'metadata'
      video.addEventListener('loadedmetadata', () => {
        if (onDurationChange) {
          onDurationChange(Math.round(video.duration))
        }

        if (video.videoWidth > 0 && video.videoHeight > 0) {
          const nextMetadata = classifyAspectRatio(
            video.videoWidth,
            video.videoHeight,
          )
          setMetadata(nextMetadata)
          onMetadataChange?.(nextMetadata)
        }

        URL.revokeObjectURL(url)
      })
      video.addEventListener('error', () => {
        URL.revokeObjectURL(url)
      })
    },
    [onChange, onDurationChange, onMetadataChange, SIZE_LIMIT_BYTES],
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const handleRemove = () => {
    onChange(null)
    setMetadata(null)
    onMetadataChange?.(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const formatBytes = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="min-w-0 space-y-3">
      {!value ? (
        <>
          <div
            onDragOver={disabled ? undefined : handleDragOver}
            onDragLeave={disabled ? undefined : handleDragLeave}
            onDrop={disabled ? undefined : handleDrop}
            onClick={disabled ? undefined : () => inputRef.current?.click()}
            className={`relative flex min-w-0 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-5 transition-colors sm:p-10 ${
              disabled
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer'
            } ${
              sizeError
                ? 'border-destructive bg-destructive/5'
                : isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-300 bg-gray-50 hover:border-primary hover:bg-primary/5'
            }`}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
              <Upload className="h-6 w-6 text-gray-500" />
            </div>
            <div className="min-w-0 text-center">
              <p className="text-sm font-medium text-black">
                ลากและวางไฟล์ MP4 ที่นี่
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                หรือคลิกเพื่อเลือกไฟล์ · รองรับ MP4 เท่านั้น · สูงสุด{' '}
                {SIZE_LIMIT_MB} MB
              </p>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="video/mp4"
              className="hidden"
              disabled={disabled}
              onChange={handleInputChange}
            />
          </div>
          {sizeError && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>ขนาดไฟล์เกิน {SIZE_LIMIT_MB} MB กรุณาเลือกไฟล์ที่เล็กกว่า</span>
            </div>
          )}
        </>
      ) : (
        <div className="min-w-0 space-y-3 rounded-xl border bg-gray-50 p-3 sm:p-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Video className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium text-black">
                {value.name}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {formatBytes(value.size)}
              </p>
              {metadata && (
                <p className="mt-1 truncate text-xs font-medium text-emerald-700">
                  {getAspectLabel(metadata)}
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={disabled || uploadProgress !== null}
              className="shrink-0 h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {uploadProgress !== null && (
            <div className="space-y-1">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-100"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-right text-xs text-muted-foreground">
                {uploadProgress < 100 ? `${Math.round(uploadProgress)}%` : 'เสร็จสิ้น'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
